/**
 * Feedback API Client — spec-feedback-rewire.
 *
 * Message-rating feedback (thumbs up/down + optional written comment)
 * against the agent-harness `/api/feedback` routes. Replaces the legacy
 * N8N `/api/ai/feedback` client (`ai.ts#submitFeedback`, now deprecated).
 *
 * Ratings reference persisted `harness_messages.id` UUIDs only — the chat
 * client receives them via the `data-persisted-message` stream part (live
 * streams) or as the message id itself (history-loaded sessions).
 */

import { harnessClient } from './client';

/** Feedback rating type. */
export type FeedbackRating = 'positive' | 'negative';

/** Request payload for POST /api/feedback. */
export interface FeedbackSubmitRequest {
  /** Persisted harness_messages.id UUID of the assistant message. */
  message_id: string;
  /** harness_sessions.id UUID the message belongs to. */
  session_id: string;
  /** Rating: positive (thumbs up) or negative (thumbs down). */
  rating: FeedbackRating;
  /** Optional written comment (allowed for BOTH ratings), max 500 chars. */
  feedback_text?: string;
}

/** Response body from POST /api/feedback. */
export interface FeedbackSubmitResponse {
  data: {
    /** UUID of the created/updated feedback record. */
    feedback_id: string;
    /** Confirmation that feedback was recorded. */
    recorded: boolean;
  };
}

/** One session rating, as returned by GET /api/feedback?session_id=. */
export interface SessionFeedbackEntry {
  message_id: string;
  rating: FeedbackRating;
  feedback_text: string | null;
}

/**
 * Submit (upsert) a rating for an assistant message. Re-submitting the same
 * `(session_id, message_id)` pair updates the rating in place; a rating
 * change without a new comment preserves the stored comment.
 */
export async function submitFeedback(
  request: FeedbackSubmitRequest,
): Promise<FeedbackSubmitResponse> {
  // harnessClient does NOT unwrap responses — read response.data ourselves.
  const response = await harnessClient.post<FeedbackSubmitResponse>(
    '/api/feedback',
    {
      message_id: request.message_id,
      session_id: request.session_id,
      rating: request.rating,
      ...(request.feedback_text ? { feedback_text: request.feedback_text } : {}),
    },
  );
  return response.data;
}

/**
 * Fetch all ratings for a session — hydration on session load so thumbs
 * (and stored comments) persist across reloads. A foreign or unknown
 * session returns an empty list.
 */
export async function getSessionFeedback(
  sessionId: string,
): Promise<SessionFeedbackEntry[]> {
  const response = await harnessClient.get<{ data: SessionFeedbackEntry[] }>(
    '/api/feedback',
    { params: { session_id: sessionId } },
  );
  return response.data.data;
}

/* ---------------------------------------------------------------- */
/* Story 36.2 — task ratings (routes shipped by Story 36.1)          */
/* ---------------------------------------------------------------- */

/** The caller's own rating of a task, as returned by GET /api/feedback/task/:taskId. */
export interface TaskFeedbackEntry {
  task_id: string;
  rating: FeedbackRating;
  feedback_text: string | null;
}

/** Request payload for POST /api/feedback/task. */
export interface TaskFeedbackSubmitRequest {
  task_id: string;
  rating: FeedbackRating;
  /** Optional comment, max 500 chars. Omitted → the server keeps the stored comment. */
  feedback_text?: string;
}

/**
 * Rate a complete task (one row per task and user; re-rating updates it).
 * `skipErrorToast` — the rating control shows its own inline error.
 */
export async function submitTaskFeedback(
  request: TaskFeedbackSubmitRequest,
): Promise<FeedbackSubmitResponse> {
  const response = await harnessClient.post<FeedbackSubmitResponse>(
    '/api/feedback/task',
    {
      task_id: request.task_id,
      rating: request.rating,
      ...(request.feedback_text ? { feedback_text: request.feedback_text } : {}),
    },
    { skipErrorToast: true },
  );
  return response.data;
}

/** The caller's own rating of a task, or null when they have not rated it. */
export async function getMyTaskFeedback(taskId: string): Promise<TaskFeedbackEntry | null> {
  const response = await harnessClient.get<{ data: TaskFeedbackEntry | null }>(
    `/api/feedback/task/${encodeURIComponent(taskId)}`,
    { skipErrorToast: true },
  );
  return response.data.data;
}

/* ---------------------------------------------------------------- */
/* Story 36.3 — app feedback (route shipped by Story 36.1)           */
/* ---------------------------------------------------------------- */

/** Optional category of an app feedback submission. */
export type AppFeedbackCategory = 'problem' | 'idea' | 'other';

/**
 * The auto-attached context (ruling R5 allow-list; the server schema is
 * `.strict()`, so no other key may be sent). Optional ids are omitted, never
 * `null`.
 */
export interface AppFeedbackContext {
  /** `location.pathname` only — no query string, no hash. */
  route: string;
  task_id?: string;
  session_id?: string;
  /** The SPA exposes no build stamp to the client, so this is `null`. */
  build_id: string | null;
  viewport: 'mobile' | 'desktop';
}

/** Request payload for POST /api/feedback/app. */
export interface AppFeedbackSubmitRequest {
  /** 1–2000 characters after trim. */
  feedback_text: string;
  category?: AppFeedbackCategory;
  context: AppFeedbackContext;
}

/**
 * Send free-text feedback to the Amatis team. `skipErrorToast` — the sheet
 * shows its own inline error.
 */
export async function submitAppFeedback(
  request: AppFeedbackSubmitRequest,
): Promise<FeedbackSubmitResponse> {
  const response = await harnessClient.post<FeedbackSubmitResponse>(
    '/api/feedback/app',
    {
      feedback_text: request.feedback_text,
      ...(request.category ? { category: request.category } : {}),
      context: request.context,
    },
    { skipErrorToast: true },
  );
  return response.data;
}
